import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Divider,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Card as CardType } from '../types';
import Card from '../components/Card';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<CardType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayCount, setDisplayCount] = useState(8);
  const muiTheme = useTheme();
  const { mode } = useCustomTheme();
  
  // Determine colors based on theme mode
  const isLightMode = mode === 'light';
  const subtitleColor = isLightMode ? muiTheme.palette.primary.main : '#FFFFFF';

  useEffect(() => {
    // Load homepage cards from localStorage
    const loadCards = () => {
      try {
        setLoading(true);
        
        // Load cards from localStorage
        const savedCards = localStorage.getItem('adminCards');
        if (savedCards) {
          const allCards = JSON.parse(savedCards);
          // Filter for cards marked as homepage cards
          const homepageCards = allCards.filter((card: CardType) => card.isHomepage);
          setCards(homepageCards);
        } else {
          setCards([]);
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to load cards. Please try again later.');
        setCards([]);
      } finally {
        setLoading(false);
      }
    };

    loadCards();
  }, []);

  const handleCardClick = (card: CardType) => {
    // Navigate to card detail page
    navigate(`/card/${card.id}`);
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => Math.min(prev + 8, cards.length));
  };

  const displayedCards = cards.slice(0, displayCount);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `
            linear-gradient(rgba(10, 16, 25, 0.8), rgba(17, 24, 39, 0.8)),
            url('/goat-cards-logo.jpg') center/cover no-repeat
          `,
          py: { xs: 8, md: 12 },
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          minHeight: { xs: '400px', md: '500px' },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              textAlign: 'center',
              position: 'relative',
              zIndex: 2,
              maxWidth: 800,
              mx: 'auto',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                color: 'white',
                fontSize: { xs: '2.5rem', md: '4rem' },
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
              }}
            >
              GOAT CARDS
            </Typography>
            <Typography
              variant="h4"
              sx={{ 
                mb: 4, 
                fontWeight: 600,
                color: subtitleColor // Now it will be white in dark mode
              }}
            >
              A new way to collect
            </Typography>
            <Typography
              variant="body1"
              sx={{ 
                mb: 5, 
                maxWidth: 600, 
                mx: 'auto', 
                fontSize: '1.1rem',
                color: '#FFFFFF', // Always white since hero has dark background
              }}
            >
              Discover hot and trending players and teams to buy. Find graded gems to raw cards from your favorite players and teams.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/cards')}
              sx={{
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: '8px',
                boxShadow: '0 4px 14px 0 rgba(49, 130, 206, 0.4)',
              }}
            >
              Start Browsing
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Featured Cards Section */}
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: 'center',
              letterSpacing: '-0.02em',
              color: isLightMode ? '#000000' : '#FFFFFF', // Ensure proper color in both modes
            }}
          >
            The Goat Collection
          </Typography>
          <Typography
            variant="body1"
            sx={{ 
              textAlign: 'center', 
              mb: 2, 
              fontSize: '1.1rem',
              color: isLightMode ? '#4A5568' : '#FFFFFF', // Ensure proper color in both modes
            }}
          >
            Premium sports cards from the Goat Cards collection
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 4, maxWidth: '1200px', mx: 'auto' }}>
              {displayedCards.map((card) => (
                <Box key={card.id}>
                  {card && card.id ? (
                    <Card card={card} onCardClick={handleCardClick} />
                  ) : (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      Invalid card data
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {displayCount < cards.length && (
              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleLoadMore}
                  sx={{ 
                    px: 5, 
                    py: 1.5, 
                    borderRadius: '8px',
                    color: isLightMode ? '#000000' : '#FFFFFF', // Ensure proper text color
                    borderColor: isLightMode ? '#000000' : '#FFFFFF', // Ensure proper border color
                  }}
                >
                  Load More Cards
                </Button>
              </Box>
            )}
          </>
        )}
        
        <Divider sx={{ my: 8 }} />
        
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} // Ensure proper text color
          >
            Live eBay Listings
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 3,
              color: isLightMode ? '#4A5568' : '#FFFFFF' // Ensure proper text color
            }}
          >
            Discover the latest sports card listings directly from eBay. Browse current auctions and buy-it-now items with real-time pricing and availability.
          </Typography>
          <Button 
            variant="outlined"
            onClick={() => navigate('/ebay-listings')}
            sx={{ 
              color: isLightMode ? '#000000' : '#FFFFFF', // Black text in light mode, white text in dark mode
              borderColor: isLightMode ? '#000000' : '#FFFFFF', // Black border in light mode, white border in dark mode
              '&:hover': {
                borderColor: isLightMode ? '#000000' : '#FFFFFF',
                backgroundColor: isLightMode ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)',
              }
            }}
          >
            View Live Listings
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage; 